import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bot, CheckCircle2, AlertCircle, Trash2, Send, ChevronDown, ChevronUp, Copy } from "lucide-react";

export default function TelegramBotManager() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ bot_token: "", chat_id: "" });

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/telegram/status");
      setStatus(data);
    } catch (e) {
      setStatus({ registered: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const isRegistered = status && status.bot_username;

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/register", {
        bot_token: form.bot_token.trim(),
        chat_id: form.chat_id.trim(),
      });
      setSuccess(`✅ Bot @${data.bot_username} conectado. Abre Telegram y escribe /start.`);
      setForm({ bot_token: "", chat_id: "" });
      await loadStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Error al registrar");
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("¿Desconectar el bot? Tendrás que volver a registrarlo después.")) return;
    setBusy(true); setError(""); setSuccess("");
    try {
      await api.delete("/admin/telegram/register");
      setSuccess("Bot desconectado.");
      await loadStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleTestMessage = async () => {
    setBusy(true); setError(""); setSuccess("");
    try {
      // Reuse the register endpoint with the same data — server will resend the welcome message.
      // Cheaper option: just open Telegram on the user's side. We'll instruct in UI instead.
      setSuccess("Abre Telegram y envía /start a tu bot para recibir el menú.");
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(`${label} copiado al portapapeles.`);
      setTimeout(() => setSuccess(""), 2500);
    } catch {
      /* noop */
    }
  };

  if (loading) {
    return (
      <div className="card-glass p-4 mb-6" data-testid="telegram-bot-loading">
        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>Cargando estado del bot…</div>
      </div>
    );
  }

  return (
    <div className="card-glass p-5 mb-6" data-testid="telegram-bot-manager">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="telegram-bot-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: isRegistered ? "rgba(34,197,94,0.12)" : "rgba(200,16,46,0.12)" }}>
            <Bot size={20} style={{ color: isRegistered ? "rgb(34,197,94)" : "var(--brand-primary)" }} />
          </div>
          <div>
            <div className="font-serif text-lg" style={{ color: "var(--text-primary)" }}>Bot de Telegram</div>
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
              {isRegistered ? (
                <>
                  <span style={{ color: "rgb(34,197,94)" }}>● Activo</span>
                  {" · "}@{status.bot_username}
                </>
              ) : (
                <>
                  <span style={{ color: "var(--brand-primary)" }}>● No registrado</span>
                  {" · Conecta tu propio bot"}
                </>
              )}
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp size={18} style={{ color: "var(--text-secondary)" }} /> : <ChevronDown size={18} style={{ color: "var(--text-secondary)" }} />}
      </button>

      {expanded && (
        <div className="mt-5 pt-5 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="telegram-bot-panel">
          {error && (
            <div className="mb-4 p-3 rounded text-xs flex items-start gap-2" style={{ background: "rgba(200,16,46,0.1)", color: "var(--brand-primary)" }} data-testid="telegram-bot-error">
              <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 rounded text-xs flex items-start gap-2" style={{ background: "rgba(34,197,94,0.1)", color: "rgb(34,197,94)" }} data-testid="telegram-bot-success">
              <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> {success}
            </div>
          )}

          {isRegistered ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <Field label="Bot username" value={`@${status.bot_username}`} onCopy={() => copyText(`@${status.bot_username}`, "Username")} />
                <Field label="Tu Chat ID" value={String(status.chat_id)} onCopy={() => copyText(String(status.chat_id), "Chat ID")} />
                <Field label="Rol del bot" value={status.admin_role} />
                <Field label="Webhook" value={status.webhook_url} mono onCopy={() => copyText(status.webhook_url, "Webhook URL")} />
              </div>

              <div className="rounded-lg p-3 text-xs" style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)" }}>
                <div className="font-medium mb-2" style={{ color: "var(--text-primary)" }}>Comandos disponibles:</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1" style={{ color: "var(--text-secondary)" }}>
                  <div><code className="text-[11px]">/nueva</code> — Crear perfil en 1 mensaje</div>
                  <div><code className="text-[11px]">/subirfotos &lt;id&gt;</code> — Añadir fotos</div>
                  <div><code className="text-[11px]">/subir</code> — Asistente completo</div>
                  <div><code className="text-[11px]">/lista</code> — Últimos 10 perfiles</div>
                  <div><code className="text-[11px]">/stats</code> — Dashboard</div>
                  <div><code className="text-[11px]">/aprobar &lt;id&gt;</code> · <code className="text-[11px]">/rechazar &lt;id&gt;</code></div>
                  <div><code className="text-[11px]">/verificar &lt;id&gt;</code> · <code className="text-[11px]">/vip &lt;id&gt;</code></div>
                  <div><code className="text-[11px]">/editar &lt;id&gt;</code> · <code className="text-[11px]">/eliminar &lt;id&gt;</code></div>
                  <div><code className="text-[11px]">/buscar &lt;texto&gt;</code></div>
                  <div><code className="text-[11px]">/fotos</code> · <code className="text-[11px]">/reportes</code></div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary text-xs inline-flex items-center gap-2"
                  onClick={handleTestMessage}
                  disabled={busy}
                  data-testid="telegram-bot-test-btn"
                >
                  <Send size={13} /> Cómo probar
                </button>
                <button
                  type="button"
                  className="btn-secondary text-xs inline-flex items-center gap-2"
                  style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}
                  onClick={handleDisconnect}
                  disabled={busy}
                  data-testid="telegram-bot-disconnect-btn"
                >
                  <Trash2 size={13} /> Desconectar
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="telegram-bot-register-form">
              <div className="rounded-lg p-3 text-xs leading-relaxed" style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)", color: "var(--text-secondary)" }}>
                <strong style={{ color: "var(--text-primary)" }}>Cómo crear tu bot:</strong>
                <ol className="list-decimal pl-5 mt-2 space-y-1">
                  <li>Abre Telegram y busca <code>@BotFather</code>.</li>
                  <li>Escribe <code>/newbot</code> y sigue las instrucciones (nombre + username).</li>
                  <li>Copia el <strong>HTTP API Token</strong> que te dará y pégalo abajo.</li>
                  <li>Para tu <strong>Chat ID</strong>: abre <code>@userinfobot</code> y escribe <code>/start</code>. Te dirá tu número.</li>
                </ol>
              </div>

              <div>
                <label className="block text-xs mb-2" style={{ color: "var(--text-secondary)" }}>Bot Token (BotFather)</label>
                <input
                  type="text"
                  required
                  className="w-full bg-transparent border rounded px-3 py-2 text-sm"
                  style={{ borderColor: "rgba(255,255,255,0.12)", color: "var(--text-primary)" }}
                  placeholder="123456789:ABCdefGhI..."
                  value={form.bot_token}
                  onChange={(e) => setForm({ ...form, bot_token: e.target.value })}
                  data-testid="telegram-bot-token-input"
                />
              </div>
              <div>
                <label className="block text-xs mb-2" style={{ color: "var(--text-secondary)" }}>Tu Chat ID (numérico)</label>
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  pattern="-?[0-9]+"
                  className="w-full bg-transparent border rounded px-3 py-2 text-sm"
                  style={{ borderColor: "rgba(255,255,255,0.12)", color: "var(--text-primary)" }}
                  placeholder="123456789"
                  value={form.chat_id}
                  onChange={(e) => setForm({ ...form, chat_id: e.target.value })}
                  data-testid="telegram-bot-chatid-input"
                />
              </div>
              <button
                type="submit"
                className="btn-primary text-xs inline-flex items-center gap-2"
                disabled={busy}
                data-testid="telegram-bot-register-btn"
              >
                <Bot size={14} /> {busy ? "Conectando…" : "Conectar bot"}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono, onCopy }) {
  return (
    <div className="rounded p-2.5" style={{ background: "rgba(255,255,255,0.02)" }}>
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-secondary)" }}>{label}</div>
      <div className="flex items-center gap-2">
        <div className={`flex-1 truncate ${mono ? "font-mono text-[11px]" : "text-xs"}`} style={{ color: "var(--text-primary)" }} title={value}>{value}</div>
        {onCopy && (
          <button type="button" onClick={onCopy} className="opacity-60 hover:opacity-100 transition-opacity" title="Copiar">
            <Copy size={12} style={{ color: "var(--text-secondary)" }} />
          </button>
        )}
      </div>
    </div>
  );
}
